import { BaseEntity, type ValidationResult } from '@memberjunction/core';
import { RegisterClass } from '@memberjunction/global';
import { FeedbackPlatformParticipantEntity } from 'mj_generatedentities';
import { DecideForeignKeysUnchanged, DecideParentReadable, DecideParticipantChange, SameRuleValue } from '../rules/decisions.js';
import { CreateOEPRuleData, ToRuleScalar } from '../rules/data.js';
import { IsStaffOrSystem } from '../rules/user.js';
import { OEP_ENTITY } from '../contracts/entities.js';
import { Apply } from './shared.js';

@RegisterClass(BaseEntity, OEP_ENTITY.Participants)
export class OEPParticipantEntity extends FeedbackPlatformParticipantEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        if (!this.IsSaved) return result; // creation is staff/seed only (no participant Create grant)
        const user = this.ActiveUser;
        const staff = IsStaffOrSystem(user);
        if (staff) return result;
        const data = CreateOEPRuleData(this.RunViewProviderToUse, user);
        // Stored columns only (no PK, no MJ audit columns, no view-joined virtual fields).
        const columns = this.Fields.filter((f) => !f.IsPrimaryKey && !f.Name.startsWith('__mj_') && !f.EntityFieldInfo.IsVirtual);
        // "Changed" is decided against a fresh DB read, never the client-supplied OldValue / Dirty flag.
        const current = await data.CurrentRow(OEP_ENTITY.Participants, this.ID, columns.map((f) => f.Name));
        if (!current) {
            Apply(result, DecideForeignKeysUnchanged({ IsStaffOrSystem: false, IsNew: false, Current: null, Proposed: {}, Fields: [], SaverUserID: user?.ID ?? '' }), this.ID);
            return result;
        }
        const changed = columns.filter((f) => !SameRuleValue(current[f.Name], ToRuleScalar(f.Value))).map((f) => f.Name);
        if (changed.length === 0) return result;
        const seasonID = current['SeasonID'];
        const season = typeof seasonID === 'string' ? await data.SeasonPhase(seasonID) : null;
        Apply(result, DecideParentReadable({ IsStaffOrSystem: false, Found: season !== null, Field: 'SeasonID' }), typeof seasonID === 'string' ? seasonID : null);
        if (!season) return result;
        Apply(result, DecideParticipantChange({ Phase: season.Phase, IsStaffOrSystem: false, ChangedFields: changed }), changed.join(','));
        return result;
    }
}
